import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { formStyles } from '../../../constants/SiteVariables';
import { getRoute, loading } from '../../../actions/sessions';
import { asyncActions, clearErrors } from '../../../actions/general';
import { createPaymentMethodCreditCard } from '../../../actions/payment_methods';
import Heap from '../../../lib/heap';

import Loading from '../../shared/Loading';
import PaymentMethodRecord from '../../../records/payment_method';
import useSessionEntity from '../../../hooks/useSessionEntity'
import Error from '../../shared/Error';
import AcceptBlueHostedForm from '../../third_party/accept_blue_hosted_form';

const heap = new Heap('CreditCardCredentials');

export default function CreditCardCredentials() {
  const dispatch = useDispatch();

  const merchant = useSessionEntity('merchant');
  const user = useSessionEntity('user');
  const isLoading = useSelector(state => state.sessions.get('loading'));
  const session = useSelector(state => state.sessions.get('session'));
  const asyncErrors = useSelector(state => state.general.get('errors'));
  const [agreed, setAgreed] = useState(false);
  const [valid, setValid] = useState(true);
  const [cardForm, setCardForm] = useState(undefined);
  const [loadCardForm, setLoadCardForm] = useState(true);
  const [formInteractedWith, setFormInteractedWith] = useState(false);
  const [cardFormErrors, setCardFormErrors] = useState([]);

  useEffect(() => {
    dispatch(loading(false));
  }, []);

  useEffect(() => {
    // Toggle loading state so in modal mode the iframe resizes properly
    dispatch(loading(!cardForm));
  }, [cardForm]);

  useEffect(() => {
    if (agreed) setValid(true);
  }, [agreed]);

  const save = async () => {
    if (!agreed) {
      setValid(false);
      return;
    }

    try {
      setCardFormErrors([]);
      dispatch(loading(true));

      const processor_data = await cardForm.getNonceToken();

      return dispatch(asyncActions(
        createPaymentMethodCreditCard(
          new PaymentMethodRecord({
            user_id: user.id,
            processor_data
          }),
        ),
        getRoute()
      ))
        .then(() => dispatch(loading(false)));
    } catch (err) {
      // Error from our API
      if (err.json) {
        err.json().then((data) => setCardFormErrors([data.error]));
      }

      // Error from AcceptBlue hosted form
      if (err.message) setCardFormErrors([err.message]);
      dispatch(loading(false));
    }
  };

  if (!user) return <Loading active={true} />;

  return (
    <div id="content">
      <div className="has-text-centered mb-5">
        <div className="is-size-4 has-text-weight-semibold">Last Step!</div>
        Please confirm your credit card details:
      </div>

      <Error />

      <div className="columns">
        <div className="column">
          <AcceptBlueHostedForm
            setFormInteractedWith={setFormInteractedWith}
            setCardForm={setCardForm}
            cardForm={cardForm}
            loadCardForm={loadCardForm}
            setCardFormErrors={setCardFormErrors}
          />

          <div role="error"
            className={classNames('notification is-danger', {
              'is-hidden': cardFormErrors.length === 0
            })}>
            {cardFormErrors.join(' - ')}
          </div>

          <div className="columns is-mobile mb-0">
            <div className="column">
              <div className="field">
                <input type="checkbox"
                  className={classNames('is-checkradio', {
                    'is-danger': !valid
                  })}
                  id="agree"
                  name="agree"
                  value={agreed}
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                />
                <label htmlFor="agree">
                  I authorize Credit Key to charge scheduled charges to my credit card.
                </label>
                {!valid && <div className={formStyles.validation}>
                  * Required
                </div>}
              </div>
            </div>
          </div>

          <button
            type="button"
            role="button"
            onClick={save}
            disabled={cardFormErrors.length > 0 || formInteractedWith === false || isLoading}
            className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
              'is-loading': isLoading
            })}>
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
